import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Send, Trash2 } from "lucide-react";
import { api, EMAIL_RE, errMsg } from "@/lib/api";
import { PageLoader } from "@/lib/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AdminLayout, btnPrimary, btnSecondary, ConfirmDelete, FieldError, inputCls, Panel } from "@/components/admin/AdminLayout";

const AlertEmails = ({ emails, onSave }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const add = async (e) => {
    e.preventDefault();
    const v = value.trim().toLowerCase();
    if (!EMAIL_RE.test(v)) return setError("Enter a valid email");
    setError("");
    if (await onSave([...emails.filter((x) => x !== v), v])) setValue("");
  };
  return (
    <>
      {emails.length === 0 ? (
        <p className="mb-3 rounded-md border border-[#C8322B]/50 bg-[#C8322B]/10 p-3 text-sm font-medium text-[#ff8a80]" data-testid="alert-emails-empty">No one will get alerts — add an email</p>
      ) : (
        <ul className="mb-3 space-y-2" data-testid="alert-emails-list">
          {emails.map((e, i) => (
            <li key={e} className="flex items-center gap-2 rounded-md border border-zinc-800 bg-[#121214] p-2 pl-3" data-testid={`alert-email-${i}`}>
              <span className="min-w-0 flex-1 break-all text-zinc-100">{e}</span>
              <ConfirmDelete description={`${e} will stop getting alerts.`} confirmLabel="Yes, remove" onConfirm={() => onSave(emails.filter((x) => x !== e))}
                trigger={<Button className="h-11 w-11 shrink-0 border border-zinc-700 bg-transparent p-0 text-zinc-200 hover:bg-zinc-800" aria-label={`Remove ${e}`} data-testid={`alert-email-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>} />
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} noValidate className="flex gap-2">
        <Input type="email" placeholder="name@example.com" value={value} onChange={(e) => setValue(e.target.value)} className={`${inputCls} min-w-0 flex-1`} data-testid="alert-email-input" />
        <Button type="submit" className={btnPrimary} data-testid="alert-email-add-button"><Plus className="h-4 w-4" /> Add</Button>
      </form>
      <FieldError msg={error} testId="alert-email-error" />
    </>
  );
};

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [testMsg, setTestMsg] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/admin/notifications").then((r) => alive && setS((cur) => cur ?? r.data));
    return () => {
      alive = false;
    };
  }, []);

  if (!s) return <PageLoader testId="settings-loading" />;
  const save = async (patch, msg) => {
    try {
      setS((await api.put("/admin/notifications", patch)).data);
      toast.success(msg || "Saved");
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const sendTest = async () => {
    setSending(true);
    setTestMsg(null);
    try {
      setTestMsg({ ok: true, text: (await api.post("/admin/notifications/test")).data.message });
    } catch (e) {
      setTestMsg({ ok: false, text: errMsg(e, "Couldn't send the test email — please try again") });
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout title="Settings" testId="admin-settings-page">
      <Panel title="Who gets email alerts" testId="alert-emails-panel">
        <p className="mb-3 text-sm text-zinc-400">These addresses get an email for every new message and review.</p>
        <AlertEmails emails={s.alert_emails} onSave={(alert_emails) => save({ alert_emails }, "Alert emails saved")} />
        <div className="mt-5 border-t border-zinc-800 pt-4">
          <Button onClick={sendTest} disabled={sending || s.alert_emails.length === 0} className={`${btnSecondary} w-full sm:w-auto`} data-testid="send-test-email-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test email
          </Button>
          {testMsg && <p className={`mt-2 text-sm font-medium ${testMsg.ok ? "text-[#7ee08a]" : "text-[#ff6b5e]"}`} data-testid="test-email-message" role="status">{testMsg.text}</p>}
        </div>
      </Panel>
      <Panel title="Reviews" testId="review-approval-panel">
        <label className="flex items-center justify-between gap-4">
          <span>
            <span className="block text-base font-medium text-zinc-100">Approve reviews before they go live</span>
            <span className="block text-sm text-zinc-400">{s.review_approval ? "New customer reviews wait in Pending until you approve them." : "New customer reviews show on the website right away."}</span>
          </span>
          <Switch checked={s.review_approval} onCheckedChange={(v) => save({ review_approval: v }, v ? "Approval turned on" : "Approval turned off")} className="scale-125" data-testid="review-approval-switch" />
        </label>
      </Panel>
    </AdminLayout>
  );
}
